import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useState } from "react";

export interface FloatingThought {
  id: string;
  text: string;
  x: number;
  y: number;
}

// Cast motion components to any to resolve TypeScript errors with framer-motion props
// Cast motion components to any to resolve TypeScript errors with framer-motion props
const MotionDiv = motion.div as any;

interface BioCanvasProps {
  volume: number; // 0-1
  mode: "normal" | "stealth";
  thoughts: FloatingThought[];
  variant?: "zen" | "chicky";
}

const BioCanvas: React.FC<BioCanvasProps> = ({ volume, mode, thoughts, variant = "zen" }) => {
  const [smoothVol, setSmoothVol] = useState(0);

  useEffect(() => {
    let animationFrameId: number;
    const animate = () => {
      setSmoothVol((prev) => prev + (volume - prev) * 0.1);
      animationFrameId = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(animationFrameId);
  }, [volume]);

  if (mode === "stealth") return null;

  // Scale calculations
  const speakingScale = 1 + smoothVol * 2.5;

  // Define palettes
  const palettes = {
    zen: {
      bgGradient: "from-[#1c1917] via-[#0a0a09] to-[#0a0a09]",
      blob1: "bg-[#a8a29e]", // Stone
      blob2: "bg-[#78716c]", // Moss/Earth
      blob3: "bg-[#e7e5e4]", // Milk/Light
      blob4: "bg-[#d6d3d1]", // Ethereal
      textColor: "text-[#e7e5e4]/90",
    },
    chicky: {
      bgGradient: "from-orange-950 via-black to-black", // Deep rich base, less muddy brown
      blob1: "bg-orange-500", // Vibrant Orange
      blob2: "bg-amber-400", // Bright Amber (Gold)
      blob3: "bg-orange-300", // Soft Orange Highlight
      blob4: "bg-rose-500", // Warm accent (Red/Rose) for depth
      textColor: "text-white/95", // Crisp white text
    },
  };

  const colors = palettes[variant];

  return (
    <div className="relative w-full h-full flex items-center justify-center overflow-hidden bg-black">
      {/* Dynamic Background Aura */}
      <MotionDiv
        className={`absolute inset-0 bg-gradient-to-b ${colors.bgGradient} opacity-60`}
        animate={{ opacity: 0.5 + smoothVol * 0.2 }}
      />

      {/* --- THE LIQUID SOUL --- */}
      <div className="relative w-[600px] h-[600px] flex items-center justify-center gooey-filter">
        {/* Blob 1: The Anchor */}
        <MotionDiv
          className={`absolute w-64 h-64 ${colors.blob1} rounded-full opacity-20 mix-blend-lighten`}
          animate={{
            rotate: 360,
            scale: [1, 1.1, 0.9, 1],
            borderRadius: [
              "60% 40% 30% 70% / 60% 30% 70% 40%",
              "30% 60% 70% 40% / 50% 60% 30% 60%",
              "60% 40% 30% 70% / 60% 30% 70% 40%",
            ],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />

        {/* Blob 2: The Breath */}
        <MotionDiv
          className={`absolute w-56 h-56 ${colors.blob2} rounded-full opacity-30 mix-blend-overlay`}
          animate={{
            rotate: -360,
            scale: [1, 1.15, 1],
            x: [0, 30, -30, 0],
            y: [0, -30, 30, 0],
          }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Blob 3: The Voice */}
        <MotionDiv
          className={`absolute w-48 h-48 ${colors.blob3} rounded-full mix-blend-normal blur-md`}
          animate={{
            scale: speakingScale,
            opacity: 0.6 + smoothVol,
          }}
          transition={{ type: "spring", stiffness: 200, damping: 25 }}
        />

        {/* Blob 4: Orbiting Element */}
        <MotionDiv
          className={`absolute w-20 h-20 ${colors.blob4} rounded-full opacity-20 mix-blend-plus-lighter`}
          animate={{
            x: [70, -70, 70],
            y: [70, 70, -70, -70, 70],
            scale: [1, 0.9, 1.1, 1],
          }}
          transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* --- THOUGHTS --- */}
      <AnimatePresence>
        {thoughts.map((thought) => (
          <MotionDiv
            key={thought.id}
            initial={{ opacity: 0, y: 50, filter: "blur(10px)" }}
            animate={{ opacity: 1, y: -100, filter: "blur(0px)" }}
            exit={{ opacity: 0, y: -200, filter: "blur(20px)", scale: 1.1 }}
            transition={{ duration: 6, ease: "circOut" }}
            className="absolute z-50 pointer-events-none"
            style={{ left: `${thought.x}%`, top: `${thought.y}%` }}
          >
            <h2
              className={`text-3xl md:text-5xl font-bold tracking-tight ${colors.textColor} mix-blend-overlay leading-none`}
            >
              {thought.text}
            </h2>
          </MotionDiv>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default BioCanvas;
